import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Search, FileDown, Filter, ChevronLeft, ChevronRight } from 'lucide-react';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    resource_type: '',
    user_id: '',
    start_date: '',
    end_date: ''
  });
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 50
  });
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchLogs();
    fetchUsers();
  }, [pagination]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('skip', pagination.skip.toString());
      params.append('limit', pagination.limit.toString());
      
      if (filters.action) params.append('action', filters.action);
      if (filters.resource_type) params.append('resource_type', filters.resource_type);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const response = await axiosInstance.get(`/audit-logs?${params.toString()}`);
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      setUsers(response.data);
    } catch (error) {
      // Users endpoint might fail if not admin
    }
  };

  const handleSearch = () => {
    setPagination({ ...pagination, skip: 0 });
    fetchLogs();
  };

  const handleClearFilters = () => {
    setFilters({
      action: '',
      resource_type: '',
      user_id: '',
      start_date: '',
      end_date: ''
    });
    setPagination({ ...pagination, skip: 0 });
  };

  const exportToCSV = () => {
    if (logs.length === 0) {
      toast.error('No logs to export');
      return;
    }

    const headers = ['Timestamp', 'User', 'Action', 'Resource Type', 'Resource ID', 'Details'];
    const rows = logs.map(log => [
      log.timestamp,
      log.username,
      log.action,
      log.resource_type,
      log.resource_id || '',
      JSON.stringify(log.details || {})
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Logs exported');
  };

  const getActionPillVariant = (action) => {
    if (action.includes('create')) return 'cb-pill--positive';
    if (action.includes('approve')) return 'cb-pill--positive';
    if (action.includes('delete') || action.includes('deactivate')) return 'cb-pill--negative';
    if (action.includes('reject')) return 'cb-pill--negative';
    if (action.includes('update') || action.includes('change')) return 'cb-pill--ink';
    return '';
  };

  const resourceTypes = ['auth', 'user', 'client', 'ledger', 'cycle', 'payout', 'expense', 'cash_pool'];

  const totalPages = Math.ceil(total / pagination.limit);
  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;

  return (
    <div className="p-8" data-testid="audit-logs-page">
      <div className="cb-page-header">
        <div className="flex justify-between items-end" style={{ gap: 'var(--cb-space-4)' }}>
          <div>
            <p className="cb-eyebrow">Activity Trail</p>
            <h1 className="cb-h1">Audit Logs</h1>
            <p className="cb-section-sub" style={{ marginTop: 'var(--cb-space-2)' }}>Track all system activity</p>
          </div>
          <button
            onClick={exportToCSV}
            data-testid="export-csv-btn"
            className="cb-btn-secondary"
          >
            <FileDown className="w-4 h-4" />
            Export CSV
          </button>
        </div>
        <hr className="cb-divider" />
      </div>

      {/* Filters */}
      <div className="cb-card mb-6">
        <div className="flex items-center mb-4" style={{ gap: 'var(--cb-space-2)' }}>
          <Filter className="w-5 h-5" style={{ color: 'var(--cb-ink-soft)' }} />
          <h2 className="cb-section-title" style={{ fontSize: '1rem' }}>Filters</h2>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div>
            <label className="form-label">Action</label>
            <input
              type="text"
              data-testid="filter-action"
              className="form-input"
              placeholder="e.g., login, create"
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            />
          </div>
          
          <div>
            <label className="form-label">Resource Type</label>
            <select
              data-testid="filter-resource-type"
              className="form-input"
              value={filters.resource_type}
              onChange={(e) => setFilters({ ...filters, resource_type: e.target.value })}
            >
              <option value="">All</option>
              {resourceTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="form-label">User</label>
            <select
              data-testid="filter-user"
              className="form-input"
              value={filters.user_id}
              onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
            >
              <option value="">All Users</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>{user.username}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="form-label">Start Date</label>
            <input
              type="date"
              data-testid="filter-start-date"
              className="form-input"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
            />
          </div>
          
          <div>
            <label className="form-label">End Date</label>
            <input
              type="date"
              data-testid="filter-end-date"
              className="form-input"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
            />
          </div>
        </div>
        
        <div className="flex gap-3 mt-4">
          <button
            onClick={handleSearch}
            data-testid="apply-filters-btn"
            className="cb-btn-primary"
          >
            <Search className="w-4 h-4" />
            Search
          </button>
          <button
            onClick={handleClearFilters}
            data-testid="clear-filters-btn"
            className="cb-btn-secondary"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Results Count */}
      <div className="cb-muted cb-num mb-4" style={{ fontSize: '0.8125rem' }}>
        Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, total)} of {total} logs
      </div>

      {/* Logs Table */}
      <div className="cb-card" style={{ padding: 0, overflowX: 'auto' }}>
        <table data-testid="audit-logs-table" className="cb-table" style={{ minWidth: '100%' }}>
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>User</th>
              <th>Action</th>
              <th>Resource</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center cb-muted" style={{ padding: 'var(--cb-space-7) 0' }}>Loading...</td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center cb-muted" style={{ padding: 'var(--cb-space-7) 0' }}>No logs found</td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id}>
                  <td className="cb-muted cb-num" style={{ fontSize: '0.8125rem' }}>
                    {new Date(log.timestamp).toLocaleString()}
                  </td>
                  <td style={{ fontWeight: 500 }}>{log.username}</td>
                  <td>
                    <span className={`cb-pill ${getActionPillVariant(log.action)}`}>
                      {log.action}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.875rem' }}>
                    {log.resource_type}
                    {log.resource_id && (
                      <span className="cb-muted cb-num" style={{ marginLeft: '4px', fontSize: '0.75rem' }}>({log.resource_id.slice(0, 8)}...)</span>
                    )}
                  </td>
                  <td className="cb-muted max-w-xs truncate" style={{ fontSize: '0.8125rem' }}>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <span title={JSON.stringify(log.details)}>
                        {Object.entries(log.details).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join(', ')}
                        {Object.keys(log.details).length > 2 && '...'}
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-6">
          <button
            onClick={() => setPagination({ ...pagination, skip: Math.max(0, pagination.skip - pagination.limit) })}
            disabled={currentPage === 1}
            data-testid="prev-page-btn"
            className="cb-btn-secondary"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          
          <span className="cb-muted cb-num" style={{ fontSize: '0.8125rem' }}>
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => setPagination({ ...pagination, skip: pagination.skip + pagination.limit })}
            disabled={currentPage === totalPages}
            data-testid="next-page-btn"
            className="cb-btn-secondary"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
